import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../common/Modal';
import LaboratoryOrderService from '../../services/laboratoryOrderService';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';
import { formatLaboratoryDate } from './laboratoryOrderUtils';
import './LaboratoryOrderPayments.css';

const formatCurrency = (n) => new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN'
}).format(Number(n || 0));

const getTodayInTimezone = (timezone) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone || 'America/Mexico_City',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const parts = formatter.formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
};

const LaboratoryOrderPayments = ({ order, token, onPaymentsChanged }) => {
  const { timezone } = useOrganizationTimezone();
  const payments = useMemo(() => order?.payments || [], [order]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState({ amount: '', date: '', note: '' });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [busyPaymentId, setBusyPaymentId] = useState(null);
  const dateInputRef = useRef(null);

  const openNativeDatePicker = useCallback(() => {
    try {
      if (dateInputRef.current && typeof dateInputRef.current.showPicker === 'function') {
        dateInputRef.current.showPicker();
      }
    } catch (e) {
      // ignore browsers that restrict programmatic open
    }
  }, []);

  const totalPaid = useMemo(
    () => payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0),
    [payments]
  );

  const openCreateModal = useCallback(() => {
    setEditingPayment(null);
    setFormData({ amount: '', date: getTodayInTimezone(timezone), note: '' });
    setFormError('');
    setIsModalOpen(true);
  }, [timezone]);

  const openEditModal = useCallback((payment) => {
    setEditingPayment(payment);
    setFormData({
      amount: String(payment.amount ?? ''),
      date: payment.date || '',
      note: payment.note || ''
    });
    setFormError('');
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    if (isSubmitting) return;
    setIsModalOpen(false);
    setEditingPayment(null);
    setFormError('');
  }, [isSubmitting]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const amountValue = Number(formData.amount);
    if (!formData.amount || Number.isNaN(amountValue) || amountValue < 0) {
      setFormError('Ingresa un monto valido');
      return;
    }
    if (!formData.date) {
      setFormError('La fecha es requerida');
      return;
    }

    const payload = {
      amount: amountValue,
      date: formData.date,
      note: (formData.note || '').trim()
    };

    setIsSubmitting(true);
    setFormError('');

    try {
      const nextOrder = editingPayment
        ? await LaboratoryOrderService.updatePayment(order.id, editingPayment.id, payload, token)
        : await LaboratoryOrderService.addPayment(order.id, payload, token);

      setIsModalOpen(false);
      setEditingPayment(null);
      if (onPaymentsChanged) {
        onPaymentsChanged(nextOrder);
      }
    } catch (error) {
      setFormError(error.message || 'No se pudo guardar el pago');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (payment) => {
    const confirmed = window.confirm(`Eliminar el pago de ${formatCurrency(payment.amount)} del ${formatLaboratoryDate(payment.date, timezone)}?`);
    if (!confirmed) return;

    setBusyPaymentId(payment.id);
    try {
      const nextOrder = await LaboratoryOrderService.deletePayment(order.id, payment.id, token);
      if (onPaymentsChanged) {
        onPaymentsChanged(nextOrder);
      }
    } catch (error) {
      window.alert(error.message || 'No se pudo eliminar el pago');
    } finally {
      setBusyPaymentId(null);
    }
  };

  useEffect(() => {
    if (!isModalOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !isSubmitting) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeModal, isModalOpen, isSubmitting]);

  const sortedPayments = useMemo(
    () => [...payments].sort((a, b) => {
      const left = a.date || a.createdAt || '';
      const right = b.date || b.createdAt || '';
      return right.localeCompare(left);
    }),
    [payments]
  );

  return (
    <section className="laboratory-order-info-card laboratory-order-payments">
      <div className="section-header-row">
        <div>
          <h2>Pagos</h2>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={openCreateModal}
          aria-label="Agregar pago"
        >
          <i className="fas fa-plus" aria-hidden="true"></i>
          Registrar pago
        </button>
      </div>

      <div className="laboratory-order-payments-summary">
        <div className="laboratory-order-payments-summary-card">
          <span className="laboratory-order-payments-summary-label">Total pagado</span>
          <span className="laboratory-order-payments-summary-value">{formatCurrency(totalPaid)}</span>
        </div>
        <div className="laboratory-order-payments-summary-card muted">
          <span className="laboratory-order-payments-summary-label">Pagos registrados</span>
          <span className="laboratory-order-payments-summary-value">{payments.length}</span>
        </div>
      </div>

      {sortedPayments.length === 0 ? (
        <p className="muted-copy">Sin pagos registrados</p>
      ) : (
        <ul className="laboratory-order-payments-list">
          {sortedPayments.map((payment) => (
          <li key={payment.id} className="laboratory-order-payment-item">
            <div className="laboratory-order-payment-content">
              <div className="laboratory-order-payment-meta">
                <span className="laboratory-order-payment-amount">{formatCurrency(payment.amount)}</span>
                <span className="laboratory-order-payment-date">
                  <i className="far fa-calendar" aria-hidden="true"></i>
                  {formatLaboratoryDate(payment.date, timezone)}
                </span>
              </div>
              {payment.note && (
                <p className="laboratory-order-payment-note">
                  <i className="fas fa-sticky-note" aria-hidden="true"></i>
                  {payment.note}
                </p>
              )}
            </div>
            <div className="laboratory-order-payment-actions">
              <button
                type="button"
                className="laboratory-order-payment-action edit"
                onClick={() => openEditModal(payment)}
                disabled={busyPaymentId === payment.id}
                aria-label="Editar pago"
              >
                <i className={`fas ${busyPaymentId === payment.id ? 'fa-spinner fa-spin' : 'fa-pen'}`} aria-hidden="true"></i>
                Editar
              </button>
              <button
                type="button"
                className="laboratory-order-payment-action delete"
                onClick={() => handleDelete(payment)}
                disabled={busyPaymentId === payment.id}
                aria-label="Eliminar pago"
              >
                <i className={`fas ${busyPaymentId === payment.id ? 'fa-spinner fa-spin' : 'fa-trash'}`} aria-hidden="true"></i>
                Eliminar
              </button>
            </div>
          </li>
          ))}
        </ul>
      )}

      <Modal
        isOpen={isModalOpen}
        title={editingPayment ? 'Editar pago' : 'Registrar pago'}
        onClose={closeModal}
      >
        <form className="laboratory-order-payment-form" onSubmit={handleSubmit}>
          {formError && <div className="laboratory-order-payment-form-error">{formError}</div>}

          <div className="form-group">
            <label htmlFor="laboratory-payment-amount">
              Monto (MXN) <span className="required">*</span>
            </label>
            <input
              id="laboratory-payment-amount"
              type="text"
              inputMode="decimal"
              value={formData.amount}
              onChange={(event) => setFormData((prev) => ({ ...prev, amount: event.target.value }))}
              placeholder="0.00"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="laboratory-payment-date">
              Fecha <span className="required">*</span>
            </label>
            <input
              id="laboratory-payment-date"
              type="date"
              value={formData.date}
              ref={dateInputRef}
              onClick={openNativeDatePicker}
              onChange={(event) => setFormData((prev) => ({ ...prev, date: event.target.value }))}
            />
          </div>

          <div className="form-group full-width">
            <label htmlFor="laboratory-payment-note">Nota</label>
            <textarea
              id="laboratory-payment-note"
              rows={3}
              value={formData.note}
              onChange={(event) => setFormData((prev) => ({ ...prev, note: event.target.value }))}
              placeholder="Opcional"
              maxLength={500}
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={closeModal}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? <span className="button-spinner" aria-hidden="true"></span> : 'Guardar'}
            </button>
          </div>
        </form>
      </Modal>
    </section>
  );
};

export default LaboratoryOrderPayments;
